import { Exclude, Expose, Type } from 'class-transformer';
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { JobLotPhaseReportInterface } from '../interfaces/job-lot-phase-report.interface';
import {
  IsBoolean,
  IsEnum,
  IsISO8601,
  IsNumber,
  IsOptional,
  Min,
  ValidateNested,
} from 'class-validator';
import { ContractorResourceTypeEnum } from '../../contractor/enums/contractor-resource-type.enum';
import { PhaseTypeEnum } from '../../phase/enums/phase-type.enum';
import { GeoMatchStatusEnum } from '../../common/enums/geo-match-status.enum';
import { GeographyPointDto } from '../../common/dto/geography-point.dto';
import { Point } from 'geojson';

@Exclude()
export class JobLotPhaseReportDto implements JobLotPhaseReportInterface {
  @ApiProperty({
    type: 'string',
    title: 'id',
  })
  @Expose()
  id!: string;

  @ApiProperty({
    type: 'string',
    title: 'jobId',
  })
  @Expose()
  jobId!: string;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'dateStart',
    description: 'dateStart',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateStart!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'dateApproved',
    description: 'dateApproved',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateApproved!: Date | null;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Approved?',
    description: 'True when the job phase has been approved.',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  approved!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'requested?',
    description: 'requested',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  requested!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Trim Option Selected?',
    description:
      'True when the trim option was selected for this phase in the Request Resource/Log modal',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  trimOptionSelected!: boolean;

  @ApiProperty({
    type: 'string',
    title: 'phaseName',
  })
  @Expose()
  phaseName!: string;

  @ApiProperty({
    type: 'string',
    title: 'requestingUserId',
  })
  @Expose()
  requestingUserId!: string;

  @ApiProperty({
    type: 'string',
    title: 'requestingUserFullName',
  })
  @Expose()
  requestingUserFullName!: string;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'countRequired?',
    description: 'countRequired',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  countRequired!: boolean;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Custom Date Start',
    description: 'Custom Date Start',
  })
  @IsISO8601({ strict: true })
  @Expose()
  customDateStart!: Date | null;

  @ApiProperty({
    type: 'string',
    title: 'initialContractorName',
  })
  @Expose()
  initialContractorName!: string;

  @ApiProperty({
    type: 'string',
    title: 'contractorName',
  })
  @Expose()
  contractorName!: string;

  @ApiProperty({
    type: 'string',
    title: 'approvingUserFullName',
  })
  @Expose()
  approvingUserFullName!: string;

  @ApiProperty({
    type: 'string',
    title: 'userId',
  })
  @Expose()
  userId!: string;

  @ApiProperty({
    type: 'string',
    title: 'contractorId',
  })
  @Expose()
  contractorId!: string;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'hasPhotos?',
    description: 'hasPhotos',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hasPhotos!: boolean;

  @ApiProperty({
    type: 'number',
    title: 'contractorHistoryCount',
  })
  @Expose()
  contractorHistoryCount!: number;

  @ApiProperty({
    type: 'string',
    title: 'userId',
  })
  @Expose()
  phaseResourceType!: string;

  @ApiProperty({
    type: 'string',
    title: 'userId',
  })
  @Expose()
  phaseType!: string;

  @ApiProperty({
    type: 'string',
    title: 'phaseName',
  })
  @Expose()
  priority!: number;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'initialDateStart',
    description: 'initialDateStart',
  })
  @IsISO8601({ strict: true })
  @Expose()
  initialDateStart!: Date | null;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'dateRequested',
    description: 'dateRequested',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateRequested!: Date | null;

  @ApiProperty({
    type: 'boolean',
    default: 'true',
    title: 'Required?',
    description: 'Will be true if this phase is required for the job',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  required!: boolean;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Date Assigned',
    description: 'The date the job phase was assigned',
  })
  @IsISO8601({ strict: true })
  @Expose()
  dateAssigned!: Date | null;

  @ApiPropertyOptional({
    type: () => GeographyPointDto,
    nullable: true,
    title: 'Approved Geo',
    description: 'GPS location when the phase was approved.',
  })
  @IsOptional()
  @ValidateNested()
  @Type(() => GeographyPointDto)
  @Expose()
  approvedGeo!: Point | null;

  @ApiPropertyOptional({
    title: 'Approved Geo Accuracy',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  approvedGeoAccuracy!: number | null;

  @ApiPropertyOptional({
    enum: GeoMatchStatusEnum,
    nullable: true,
    title: 'Approved Geo Status',
  })
  @IsOptional()
  @IsEnum(GeoMatchStatusEnum)
  @Expose()
  approvedGeoStatus!: GeoMatchStatusEnum | null;

  @ApiPropertyOptional({
    title: 'Approved Geo Calculated Distance',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  approvedGeoCalculatedDistance!: number | null;
}
